import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { 
  Search, Eye, Phone, FileText,
  Loader2, Calendar, Building2, AlertTriangle, Wrench
} from 'lucide-react';
import api from '../../utils/api';

const MobileContracts = () => {
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('Active');
  
  // Dialog states
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);

  const fetchContracts = useCallback(async () => {
    try {
      const response = await api.get('/contracts/');
      setContracts(response.data);
    } catch (error) {
      toast.error('Failed to fetch contracts');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchContracts();
  }, [fetchContracts]);

  // Helper functions
  const isExpiringWithinDays = (endDate, days) => {
    if (!endDate) return false;
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays >= 0 && diffDays <= days;
  };

  const getDaysUntilExpiry = (endDate) => {
    if (!endDate) return null;
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Active': return 'bg-green-500 text-white';
      case 'Expired': return 'bg-red-500 text-white';
      case 'Pending': return 'bg-yellow-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Filter contracts
  const filteredContracts = contracts.filter(contract => {
    const searchLower = searchTerm.toLowerCase();
    const matchesSearch = 
      contract.contract_no?.toLowerCase().includes(searchLower) ||
      contract.customer?.company_name?.toLowerCase().includes(searchLower) ||
      contract.customer?.mobile?.includes(searchTerm) ||
      contract.machine_device?.toLowerCase().includes(searchLower);
    
    const matchesType = typeFilter === 'all' || contract.contract_type === typeFilter;
    const matchesStatus = statusFilter === 'all' || contract.contract_status === statusFilter;
    
    return matchesSearch && matchesType && matchesStatus;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-24 space-y-4">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-xl font-bold text-gray-900">AMC / MMC Contracts</h1>
          <p className="text-sm text-gray-500">{filteredContracts.length} contracts</p>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="space-y-2">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Search by contract, customer, mobile..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        <div className="flex gap-2">
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="AMC">AMC</SelectItem>
              <SelectItem value="MMC">MMC</SelectItem>
            </SelectContent>
          </Select>
          
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="flex-1">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Expired">Expired</SelectItem>
              <SelectItem value="Pending">Pending</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Contracts List */}
      {filteredContracts.length === 0 ? (
        <Card>
          <CardContent className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-2 text-gray-300" />
            <p>No contracts found</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {filteredContracts.map((contract) => {
            const daysUntilExpiry = getDaysUntilExpiry(contract.end_date);
            const isExpiringSoon = contract.contract_type === 'AMC' && isExpiringWithinDays(contract.end_date, 30);
            
            return (
              <Card 
                key={contract.id} 
                className={`overflow-hidden ${isExpiringSoon ? 'border-orange-300 bg-orange-50' : ''}`}
              >
                <CardContent className="p-4">
                  {/* Header Row */}
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <span className="text-sm font-semibold text-indigo-600">{contract.contract_no}</span>
                      <Badge className={`ml-2 ${getStatusColor(contract.contract_status)}`}>
                        {contract.contract_status}
                      </Badge>
                      <Badge variant="outline" className={`ml-1 ${contract.contract_type === 'MMC' ? 'bg-blue-100 text-blue-800' : ''}`}>
                        {contract.contract_type}
                      </Badge>
                    </div>
                    {isExpiringSoon && (
                      <AlertTriangle className="w-5 h-5 text-orange-500" />
                    )}
                  </div>

                  {/* Customer Info */}
                  <div className="space-y-2 mb-3">
                    <div className="flex items-center gap-2">
                      <Building2 className="w-4 h-4 text-gray-400" />
                      <span className="font-medium">{contract.customer?.company_name}</span>
                    </div>
                    {contract.customer?.mobile && (
                      <a href={`tel:${contract.customer.mobile}`} className="flex items-center gap-2 text-blue-600">
                        <Phone className="w-4 h-4" />
                        <span>{contract.customer.mobile}</span>
                      </a>
                    )}
                    <div className="flex items-center gap-2 text-gray-600 text-sm">
                      <Wrench className="w-4 h-4 text-gray-400" />
                      <span>{contract.machine_device || 'N/A'}</span>
                    </div>
                  </div>

                  {/* Contract Details */}
                  <div className="bg-gray-50 rounded-lg p-2 mb-3 text-sm">
                    {contract.contract_type === 'AMC' ? (
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-gray-500">Period:</span>
                          <span className="ml-1">{new Date(contract.start_date).toLocaleDateString('en-IN')} - {new Date(contract.end_date).toLocaleDateString('en-IN')}</span>
                        </div>
                        {daysUntilExpiry !== null && (
                          <span className={`font-medium ${daysUntilExpiry <= 30 ? 'text-orange-600' : daysUntilExpiry <= 0 ? 'text-red-600' : 'text-green-600'}`}>
                            {daysUntilExpiry > 0 ? `${daysUntilExpiry} days left` : 'Expired'}
                          </span>
                        )}
                      </div>
                    ) : (
                      <div className="flex justify-between items-center">
                        <div>
                          <span className="text-gray-500">Service Day:</span>
                          <span className="ml-1 font-medium">{contract.monthly_service_day} of each month</span>
                        </div>
                        <div>
                          <span className="text-gray-500">Used:</span>
                          <span className="ml-1 font-medium">{contract.services_used || 0}/{contract.services_included || 'N/A'}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Action Buttons */}
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="flex-1"
                      onClick={() => { setSelectedContract(contract); setViewDialogOpen(true); }}
                    >
                      <Eye className="w-4 h-4 mr-1" /> View Details
                    </Button>
                    {contract.customer?.mobile && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        asChild
                      >
                        <a href={`tel:${contract.customer.mobile}`}>
                          <Phone className="w-4 h-4" />
                        </a>
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* View Contract Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Contract Details - {selectedContract?.contract_no}</DialogTitle>
          </DialogHeader>
          {selectedContract && (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Badge className={getStatusColor(selectedContract.contract_status)}>{selectedContract.contract_status}</Badge>
                <Badge variant="outline">{selectedContract.contract_type}</Badge>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Customer</p>
                  <p className="font-medium">{selectedContract.customer?.company_name}</p>
                </div>
                <div>
                  <p className="text-gray-500">Mobile</p>
                  <p className="font-medium">{selectedContract.customer?.mobile || 'N/A'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Machine/Device</p>
                  <p className="font-medium">{selectedContract.machine_device || 'N/A'}</p>
                </div>
                {selectedContract.contract_type === 'AMC' ? (
                  <>
                    <div>
                      <p className="text-gray-500">Start Date</p>
                      <p className="font-medium">{new Date(selectedContract.start_date).toLocaleDateString('en-IN')}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">End Date</p>
                      <p className="font-medium">{new Date(selectedContract.end_date).toLocaleDateString('en-IN')}</p>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <p className="text-gray-500">Service Day</p>
                      <p className="font-medium">{selectedContract.monthly_service_day} of month</p>
                    </div>
                    <div>
                      <p className="text-gray-500">Services</p>
                      <p className="font-medium">{selectedContract.services_used || 0} / {selectedContract.services_included || 'N/A'} used</p>
                    </div>
                  </>
                )}
                <div>
                  <p className="text-gray-500">Contract Value</p>
                  <p className="font-medium">₹{selectedContract.contract_value?.toLocaleString('en-IN') || '0'}</p>
                </div>
                <div>
                  <p className="text-gray-500">Created</p>
                  <p className="font-medium">{new Date(selectedContract.created_at).toLocaleDateString('en-IN')}</p>
                </div>
              </div>
              
              {selectedContract.notes && (
                <div>
                  <p className="text-gray-500 text-sm">Notes</p>
                  <p className="text-sm mt-1">{selectedContract.notes}</p>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MobileContracts;
